<script setup lang="ts">
/**
 * Carte d'un objet matériel suivi (`AssetAssignment`) dans un dossier.
 *
 * Affiche : en-tête (label + statut + catégorie), responsable, dates clés (dont
 * le rappel d'échéance mis en avant), inventaire (avec masquage des secrets via
 * `OnbSecretValue`), notes, historique repliable et une action pour faire
 * avancer le statut vers la cible (Remis / Restitué).
 */
import { computed, ref } from 'vue'
import {
  ArrowUturnLeftIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  InboxArrowDownIcon,
  PencilSquareIcon,
  ShoppingCartIcon,
  TagIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import {
  ASSET_CONDITIONS,
  ASSET_STATUS_META,
  CATEGORY_META,
  CONDITION_LABEL,
  RETURN_CONDITIONS,
  attributeLabel,
  isSensitiveAttribute,
  requiresReturnCondition,
} from '@/v2/onboarding/catalog'
import type { AssetAssignment, AssetStatus, CaseType } from '@/v2/onboarding/types'
import OnbAssetStatusBadge from '@/v2/components/onboarding/OnbAssetStatusBadge.vue'
import OnbAssetEditDialog from '@/v2/components/onboarding/OnbAssetEditDialog.vue'
import OnbSecretValue from '@/v2/components/onboarding/OnbSecretValue.vue'

const props = defineProps<{
  asset: AssetAssignment
  caseId: string
  caseType: CaseType
}>()

const store = useOnboardingStore()
const perms = useBoostCorePermissionStore()

/** Modale d'édition (admin) — pilotée par ref. */
const editDialog = ref<InstanceType<typeof OnbAssetEditDialog> | null>(null)

/** L'utilisateur peut-il administrer le matériel (modifier / supprimer) ? */
const canManage = computed(() => perms.can('MATERIAL_WRITE'))

/** Confirmation de suppression en 2 temps. */
const confirmingDelete = ref(false)
const deleting = ref(false)

/** Date ISO/jour → JJ/MM/AAAA FR (null → « — »). */
function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
}

/** Date + heure courte FR pour la timeline d'historique. */
const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
function formatDateTime(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : dateTimeFmt.format(d)
}

/** Heuristique : la valeur ressemble-t-elle à un numéro (→ tabular-nums) ? */
function looksNumeric(value: string): boolean {
  return /^[\d\s/.\-+]+$/.test(value.trim())
}

/** Dates clés à afficher si présentes (le rappel est traité à part pour le mettre en avant). */
const dateChips = computed(() =>
  (
    [
      { key: 'orderedAt', label: 'Commande', value: props.asset.orderedAt, icon: ShoppingCartIcon },
      { key: 'expectedAt', label: 'Prévue', value: props.asset.expectedAt, icon: CalendarDaysIcon },
      { key: 'deliveredAt', label: 'Remise', value: props.asset.deliveredAt, icon: CheckCircleIcon },
      { key: 'returnedAt', label: 'Restitution', value: props.asset.returnedAt, icon: InboxArrowDownIcon },
    ] as const
  ).filter((d) => !!d.value),
)

const attributeEntries = computed(() => Object.entries(props.asset.attributes))

/** Historique du plus récent au plus ancien. */
const historyDesc = computed(() => [...props.asset.history].slice().reverse())

/** Statut cible selon le sens du dossier. */
const targetStatus = computed<AssetStatus>(() => (props.caseType === 'OFFBOARDING' ? 'RETURNED' : 'DELIVERED'))
const advanceLabel = computed(() => (props.caseType === 'OFFBOARDING' ? 'Marquer comme restitué' : 'Marquer comme remis'))

/** L'action d'avancement n'a de sens que si l'objet n'est ni à la cible ni remplacé. */
const canAdvance = computed(
  () => props.asset.status !== targetStatus.value && props.asset.status !== 'REPLACED',
)

/**
 * Le matériel concerné demande-t-il un choix d'état au marquage ?
 * Vrai pour le matériel physique, à la remise (DELIVERED) comme à la restitution (RETURNED).
 * (false pour lignes/accès → avancement direct.)
 */
const needsConditionChoice = computed(
  () =>
    (targetStatus.value === 'RETURNED' || targetStatus.value === 'DELIVERED') &&
    requiresReturnCondition(props.asset.typeCode),
)

/** À la restitution, l'état est obligatoire ; à la remise, il est facultatif. */
const conditionRequired = computed(() => targetStatus.value === 'RETURNED')

/** Options proposées selon le sens : restitution (sans Reconditionné) ou remise. */
const conditionOptions = computed(() =>
  targetStatus.value === 'RETURNED' ? RETURN_CONDITIONS : ASSET_CONDITIONS,
)

const choosingCondition = ref(false)
const chosenCondition = ref('')
const advancing = ref(false)

/** Clic sur « Marquer remis/restitué » : ouvre le choix d'état si requis, sinon valide. */
function startAdvance() {
  if (needsConditionChoice.value) {
    chosenCondition.value = ''
    choosingCondition.value = true
    return
  }
  void doAdvance()
}

/** Valide l'avancement vers la cible, en transmettant l'état choisi le cas échéant. */
async function confirmAdvance() {
  // Restitution : un état est obligatoire.
  if (conditionRequired.value && !chosenCondition.value) return
  await doAdvance(chosenCondition.value)
}

async function doAdvance(condition?: string) {
  advancing.value = true
  try {
    const opts =
      targetStatus.value === 'RETURNED'
        ? condition
          ? { returnCondition: condition }
          : undefined
        : condition
          ? { condition }
          : undefined
    await store.setAssetStatus(props.asset.id, targetStatus.value, opts)
    choosingCondition.value = false
  } finally {
    advancing.value = false
  }
}

/** Ouvre la modale d'édition pré-remplie sur cet objet. */
function openEdit() {
  editDialog.value?.open(props.asset)
}

/** Supprime l'objet après confirmation (2e clic). */
async function confirmDelete() {
  if (deleting.value) return
  deleting.value = true
  try {
    await store.deleteAsset(props.asset.id)
  } finally {
    deleting.value = false
    confirmingDelete.value = false
  }
}
</script>

<template>
  <article class="card border border-base-300 bg-base-100 shadow-sm">
    <div class="card-body gap-3 p-4">
      <!-- En-tête : label + statut + catégorie -->
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div class="min-w-0">
          <h4 class="font-semibold text-base-content">{{ asset.label }}</h4>
          <p class="mt-0.5 inline-flex items-center gap-1 text-sm text-base-content/70">
            <TagIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            {{ CATEGORY_META[asset.category].label }}
          </p>
        </div>
        <OnbAssetStatusBadge :status="asset.status" size="md" />
      </div>

      <!-- Responsable -->
      <p class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-base-content/80">
        <UserIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
        <span class="text-base-content/70">Responsable :</span>
        <span class="font-medium break-words text-base-content">{{ asset.owner ?? 'Non assigné' }}</span>
      </p>

      <!-- Rappel d'échéance mis en avant -->
      <p
        v-if="asset.reminderAt"
        class="inline-flex w-fit items-center gap-1.5 rounded-field border border-warning/40 bg-warning/10 px-2 py-1 text-sm text-warning"
      >
        <BellAlertIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
        <span class="font-medium">Rappel :</span>
        <span class="tabular-nums">{{ formatDate(asset.reminderAt) }}</span>
      </p>

      <!-- Dates clés -->
      <ul v-if="dateChips.length" role="list" class="flex flex-wrap gap-2">
        <li
          v-for="d in dateChips"
          :key="d.key"
          class="inline-flex items-center gap-1.5 rounded-field bg-base-200 px-2 py-1 text-sm"
        >
          <component :is="d.icon" class="h-4 w-4 shrink-0 text-base-content/70" aria-hidden="true" />
          <span class="text-base-content/70">{{ d.label }} :</span>
          <span class="tabular-nums text-base-content">{{ formatDate(d.value) }}</span>
        </li>
      </ul>

      <!-- État du matériel (fourni à la remise / rendu à la restitution) -->
      <p
        v-if="asset.condition"
        class="inline-flex w-fit items-center gap-1.5 rounded-field bg-base-200 px-2 py-1 text-sm"
      >
        <CheckCircleIcon class="h-4 w-4 shrink-0 text-base-content/70" aria-hidden="true" />
        <span class="text-base-content/70">État fourni :</span>
        <span class="font-medium text-base-content">
          {{ CONDITION_LABEL[asset.condition] ?? asset.condition }}
        </span>
      </p>
      <p
        v-if="asset.status === 'RETURNED' && asset.returnCondition"
        class="inline-flex w-fit items-center gap-1.5 rounded-field bg-base-200 px-2 py-1 text-sm"
      >
        <InboxArrowDownIcon class="h-4 w-4 shrink-0 text-base-content/70" aria-hidden="true" />
        <span class="text-base-content/70">État rendu :</span>
        <span class="font-medium text-base-content">
          {{ CONDITION_LABEL[asset.returnCondition] ?? asset.returnCondition }}
        </span>
      </p>

      <!-- Inventaire -->
      <section aria-label="Inventaire" class="rounded-box border border-base-300 bg-base-200/40 p-3">
        <h5 class="mb-2 text-sm font-medium text-base-content/70">Inventaire</h5>
        <p v-if="!attributeEntries.length" class="text-sm text-base-content/80">
          Aucune information d'inventaire.
        </p>
        <dl v-else class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div v-for="[key, value] in attributeEntries" :key="key" class="min-w-0">
            <dt class="text-sm text-base-content/70">{{ attributeLabel(key) }}</dt>
            <dd>
              <OnbSecretValue v-if="isSensitiveAttribute(key)" :value="value" />
              <span
                v-else
                class="text-base break-words text-base-content"
                :class="{ 'tabular-nums': looksNumeric(value) }"
                >{{ value }}</span
              >
            </dd>
          </div>
        </dl>
      </section>

      <!-- Notes -->
      <p v-if="asset.notes" class="text-sm whitespace-pre-line text-base-content/80">{{ asset.notes }}</p>

      <!-- Historique repliable -->
      <details v-if="historyDesc.length" class="collapse-arrow rounded-box border border-base-300 bg-base-100">
        <summary class="cursor-pointer px-3 py-2 text-sm font-medium">
          Historique
          <span class="badge badge-sm badge-ghost tabular-nums">{{ historyDesc.length }}</span>
        </summary>
        <ol role="list" class="space-y-2 px-3 pb-3">
          <li v-for="(ev, idx) in historyDesc" :key="idx" class="flex gap-2 text-sm">
            <ClockIcon class="mt-0.5 h-4 w-4 shrink-0 text-base-content/60" aria-hidden="true" />
            <div class="min-w-0">
              <p class="text-base-content">
                <span class="font-medium">{{ ASSET_STATUS_META[ev.toStatus].label }}</span>
                <span class="text-base-content/70"> — {{ ev.by }} — </span>
                <span class="tabular-nums text-base-content/70">{{ formatDateTime(ev.at) }}</span>
              </p>
              <p v-if="ev.reason" class="text-base-content/80">{{ ev.reason }}</p>
            </div>
          </li>
        </ol>
      </details>

      <!-- Avancement de statut (Remis / Restitué) -->
      <div v-if="canAdvance" class="card-actions flex-col items-stretch">
        <button
          v-if="!choosingCondition"
          type="button"
          class="btn btn-primary motion-reduce:transition-none"
          @click="startAdvance"
        >
          <ArrowUturnLeftIcon v-if="caseType === 'OFFBOARDING'" class="h-5 w-5" aria-hidden="true" />
          <CheckCircleIcon v-else class="h-5 w-5" aria-hidden="true" />
          {{ advanceLabel }}
        </button>

        <!-- Choix d'état au marquage (matériel physique) : remise ou restitution -->
        <div v-else class="flex flex-col gap-2 rounded-box border border-base-300 bg-base-200/40 p-3">
          <label :for="`cond-${asset.id}`" class="text-sm font-medium text-base-content/80">
            {{ conditionRequired ? 'État du matériel rendu' : 'État du matériel fourni' }}
            <span v-if="conditionRequired" class="text-error" aria-hidden="true">*</span>
          </label>
          <select :id="`cond-${asset.id}`" v-model="chosenCondition" class="select select-bordered w-full">
            <option v-if="conditionRequired" value="" disabled>Sélectionner un état…</option>
            <option v-else value="">Non précisé</option>
            <option v-for="c in conditionOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
          </select>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-primary motion-reduce:transition-none"
              :disabled="(conditionRequired && !chosenCondition) || advancing"
              :aria-busy="advancing"
              @click="confirmAdvance"
            >
              <ArrowUturnLeftIcon v-if="caseType === 'OFFBOARDING'" class="h-5 w-5" aria-hidden="true" />
              <CheckCircleIcon v-else class="h-5 w-5" aria-hidden="true" />
              {{ caseType === 'OFFBOARDING' ? 'Confirmer la restitution' : 'Confirmer la remise' }}
            </button>
            <button
              type="button"
              class="btn btn-ghost motion-reduce:transition-none"
              :disabled="advancing"
              @click="choosingCondition = false"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>

      <!-- Actions admin (réservées MATERIAL_WRITE) -->
      <div v-if="canManage" class="mt-1 flex flex-wrap items-center gap-2 border-t border-base-300 pt-3">
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          @click="openEdit"
        >
          <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
          Modifier
        </button>

        <!-- Suppression : confirmation en 2 temps -->
        <template v-if="!confirmingDelete">
          <button
            type="button"
            class="btn btn-ghost btn-sm text-error motion-reduce:transition-none"
            @click="confirmingDelete = true"
          >
            <TrashIcon class="h-5 w-5" aria-hidden="true" />
            Supprimer
          </button>
        </template>
        <template v-else>
          <span class="text-sm text-base-content/80">Confirmer la suppression ?</span>
          <button
            type="button"
            class="btn btn-error btn-sm motion-reduce:transition-none"
            :disabled="deleting"
            :aria-busy="deleting"
            @click="confirmDelete"
          >
            <TrashIcon class="h-5 w-5" aria-hidden="true" />
            Confirmer
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-sm motion-reduce:transition-none"
            :disabled="deleting"
            @click="confirmingDelete = false"
          >
            Annuler
          </button>
        </template>
      </div>
    </div>

    <!-- Modale d'édition (admin) -->
    <OnbAssetEditDialog v-if="canManage" ref="editDialog" />
  </article>
</template>
